import { useTheme } from '@emotion/react';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';

import {
  toStoreCouponsLink,
  toStoreOffersLink,
  toSubcategoryLink,
} from 'lib/links';

import HEADING from 'constants/heading';

const OFFERS_LIMIT = 4;

const OfferRelatedItems = forwardRef(
  ({ relatedSubcategories, storeName, storeParam, ...rest }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const theme = useTheme();
    const relatedSubcategoriesLength = relatedSubcategories.length;

    if (relatedSubcategoriesLength === 0) {
      return null;
    }

    return (
      <Box ref={ref} px={[4, null, 6]} {...rest}>
        <Flex
          alignItems="flex-start"
          borderBottom="1px solid"
          flexDirection="column"
          pt={4}
          pb={2}
          borderColor={theme.app.offerRelatedItems.border}
        >
          <Heading size="size5" as="span" mb={4}>
            {HEADING.SEE_ALSO}
          </Heading>
          {storeParam && (
            <>
              <Flex flexDirection="column" full>
                <NextLink href={toStoreOffersLink(storeParam)} passHref>
                  <Button as="a" type="subtle" size="size4" mb={2} mr={2}>
                    Ofertas {storeName}
                  </Button>
                </NextLink>
                <NextLink
                  href={toStoreCouponsLink(storeParam)}
                  prefetch={false}
                  passHref
                >
                  <Button as="a" type="subtle" size="size4" mb={2} mr={2}>
                    Cupons {storeName}
                  </Button>
                </NextLink>
              </Flex>
            </>
          )}
          <Flex flexWrap="wrap">
            {relatedSubcategories
              .slice(0, isOpen ? relatedSubcategoriesLength : OFFERS_LIMIT)
              .map(({ subcategoryId, subcategoryName, subcategorySlug }) => (
                <NextLink
                  key={subcategoryId}
                  href={toSubcategoryLink(subcategorySlug)}
                  prefetch={false}
                  passHref
                >
                  <Button as="a" type="subtle" size="size4" mb={2} mr={2}>
                    {subcategoryName}
                  </Button>
                </NextLink>
              ))}
          </Flex>
        </Flex>
        {relatedSubcategoriesLength > OFFERS_LIMIT && (
          <Flex justifyContent="center" py={4}>
            <Link onClick={() => setIsOpen((prev) => !prev)}>
              {isOpen ? 'Fechar' : 'Expandir'}
            </Link>
          </Flex>
        )}
      </Box>
    );
  }
);

OfferRelatedItems.displayName = 'OfferRelatedItems';

OfferRelatedItems.defaultProps = {
  relatedSubcategories: [],
  storeName: null,
  storeParam: null,
};

OfferRelatedItems.propTypes = {
  relatedSubcategories: PropTypes.arrayOf(
    PropTypes.shape({
      subcategoryId: PropTypes.number,
      subcategoryName: PropTypes.string,
    })
  ),
  storeName: PropTypes.string,
  storeParam: PropTypes.string,
};

export default OfferRelatedItems;
